/**
 * Shared stat label mappings for normalizing stat names across the application.
 * Used for matching custom EV data to props and displaying consistent stat labels.
 */

/**
 * Full stat label mapping including all aliases for long stat names.
 * Maps various stat name formats to their canonical short form.
 */
export const STAT_LABELS: Record<string, string> = {
  PTS: 'PTS',
  Points: 'PTS',
  REB: 'REB',
  Rebounds: 'REB',
  AST: 'AST',
  Assists: 'AST',
  '3PM': '3PM',
  '3-Pointers': '3PM',
  Threes: '3PM',
  PRA: 'PRA',
  'Points+Rebounds+Assists': 'PRA',
  'P+A': 'P+A',
  'Points+Assists': 'P+A',
  'P+R': 'P+R',
  'Points+Rebounds': 'P+R',
  'R+A': 'R+A',
  'Rebounds+Assists': 'R+A',
  // MLB hitter stats
  H: 'H',
  Hits: 'H',
  'Total Hits': 'H',
  HR: 'HR',
  'Home Runs': 'HR',
  'Total Home Runs': 'HR',
  R: 'R',
  Runs: 'R',
  'Runs Scored': 'R',
  'Total Player Runs': 'R',
  RBI: 'RBI',
  'Runs Batted In': 'RBI',
  'Total RBI': 'RBI',
  'Total RBIs': 'RBI',
  BB: 'BB',
  Walks: 'BB',
  'Total Walks': 'BB',
  'Total Batter Walks': 'BB',
  SO: 'SO',
  Strikeouts: 'SO',
  'Batter Strikeouts': 'SO',
  'Total Batter Strikeouts': 'SO',
  SB: 'SB',
  'Stolen Bases': 'SB',
  'Total Stolen Bases': 'SB',
  TB: 'TB',
  'Total Bases': 'TB',
  '1B': '1B',
  Singles: '1B',
  'Total Singles': '1B',
  '2B': '2B',
  Doubles: '2B',
  'Total Doubles': '2B',
  '3B': '3B',
  Triples: '3B',
  'Total Triples': '3B',
  // MLB pitcher stats
  IP: 'IP',
  'Innings Pitched': 'IP',
  K: 'K',
  'Pitcher Strikeouts': 'K',
  'Total Strikeouts': 'K',
  'Strikeouts Pitched': 'K',
  ER: 'ER',
  'Earned Runs': 'ER',
  'Earned Runs Allowed': 'ER',
  'Total Earned Runs': 'ER',
  HA: 'HA',
  'Hits Allowed': 'HA',
  'Pitcher Hits Allowed': 'HA',
  'Total Hits Allowed': 'HA',
  PBB: 'PBB',
  'Pitcher Walks': 'PBB',
  'Total Pitcher Walks': 'PBB',
  OUTS: 'OUTS',
  'Pitching Outs': 'OUTS',
  'Total Pitching Outs': 'OUTS',
  // NHL stats
  G: 'G',
  Goals: 'G',
  'Total Player Goals': 'G',
  'Player Goals': 'G',
  SOG: 'SOG',
  'Shots on Goal': 'SOG',
  'Total Shots on Goal': 'SOG',
  BLK: 'BLK',
  'Blocked Shots': 'BLK',
  SV: 'SV',
  Saves: 'SV',
  'Goalie Saves': 'SV',
  // NFL stats
  'Pass Yds': 'Pass Yds',
  'Passing Yards': 'Pass Yds',
  'Passing Yds': 'Pass Yds',
  'Pass Yards': 'Pass Yds',
  'Pass TDs': 'Pass TDs',
  'Passing Touchdowns': 'Pass TDs',
  'Passing TDs': 'Pass TDs',
  Comp: 'Comp',
  'Pass Completions': 'Comp',
  'Passing Completions': 'Comp',
  Completions: 'Comp',
  'Pass Att': 'Pass Att',
  'Passing Attempts': 'Pass Att',
  'Pass Attempts': 'Pass Att',
  INT: 'INT',
  Interceptions: 'INT',
  'Interceptions Thrown': 'INT',
  'Rush Yds': 'Rush Yds',
  'Rushing Yards': 'Rush Yds',
  'Rushing Yds': 'Rush Yds',
  'Rush Yards': 'Rush Yds',
  'Rush Att': 'Rush Att',
  'Rushing Attempts': 'Rush Att',
  'Rush Attempts': 'Rush Att',
  'Rush TDs': 'Rush TDs',
  'Rushing Touchdowns': 'Rush TDs',
  Rec: 'Rec',
  Receptions: 'Rec',
  'Player Receptions': 'Rec',
  Recs: 'Rec',
  'Rec Yds': 'Rec Yds',
  'Receiving Yards': 'Rec Yds',
  'Receiving Yds': 'Rec Yds',
  'Rec Yards': 'Rec Yds',
  'Rec TDs': 'Rec TDs',
  'Receiving Touchdowns': 'Rec TDs',
  Targets: 'Targets',
  'Receiving Targets': 'Targets',
  'Long Rec': 'Long Rec',
  'Longest Reception': 'Long Rec',
  'Long Rush': 'Long Rush',
  'Longest Rush': 'Long Rush',
  'Rush+Rec Yds': 'Rush+Rec Yds',
  'Rushing + Receiving Yards': 'Rush+Rec Yds',
  'Rush + Rec Yards': 'Rush+Rec Yds',
  'Pass+Rush Yds': 'Pass+Rush Yds',
  'Passing + Rushing Yards': 'Pass+Rush Yds',
  'Pass + Rush Yards': 'Pass+Rush Yds',
  'Any TD': 'Any TD',
  'Anytime Touchdown Scorer': 'Any TD',
  'Any Time Touchdown Scorer': 'Any TD',
  'Anytime TD Scorer': 'Any TD',
  'Player Anytime TD': 'Any TD',
  'Kick Pts': 'Kick Pts',
  'Kicking Points': 'Kick Pts',
  FGM: 'FGM',
  'Field Goals Made': 'FGM',
  XPM: 'XPM',
  'Extra Points Made': 'XPM',
  'Tkl+Ast': 'Tkl+Ast',
  'Tackles + Assists': 'Tkl+Ast',
  'Total Tackles + Assists': 'Tkl+Ast',
  Tackles: 'Tackles',
  'Total Tackles': 'Tackles',
  'Def Ast': 'Def Ast',
  Sacks: 'Sacks',
  'Total Sacks': 'Sacks',
  'INT Caught': 'INT Caught',
  'Caught INTs': 'INT Caught',
  'Fum Lost': 'Fum Lost',
  'Fumbles Lost': 'Fum Lost',
  'Long Comp': 'Long Comp',
  'Longest Passing Completion': 'Long Comp',
  'Rush+Rec TDs': 'Rush+Rec TDs',
  'Total Rushing + Receiving TDs': 'Rush+Rec TDs',
  'Total TDs': 'Total TDs',
  'Total Passing + Rushing + Receiving TDs': 'Total TDs',
  'Fantasy Pts': 'Fantasy Pts',
  'Fantasy Points': 'Fantasy Pts',
  // "Total ..." forms are what the offers table actually emits for the NFL
  'Total Passing Yards': 'Pass Yds',
  'Total Passing Touchdowns': 'Pass TDs',
  'Total Pass Completions': 'Comp',
  'Total Passing Attempts': 'Pass Att',
  'Total Interceptions Thrown': 'INT',
  'Total Rushing Yards': 'Rush Yds',
  'Total Rushing Attempts': 'Rush Att',
  'Total Rushing Touchdowns': 'Rush TDs',
  'Total Receptions': 'Rec',
  'Total Receiving Yards': 'Rec Yds',
  'Total Receiving Touchdowns': 'Rec TDs',
  'Total Targets': 'Targets',
  'Total Rushing + Receiving Yards': 'Rush+Rec Yds',
  'Total Passing + Rushing Yards': 'Pass+Rush Yds',
  'Total Touchdowns': 'Any TD',
  'Total Kicking Points': 'Kick Pts',
  'Total Field Goals Made': 'FGM',
  'Total Extra Points Made': 'XPM',
};

// Normalized stat codes that should never be shown in the props tool
export const HIDDEN_STATS = new Set<string>(['OUTS', 'PBB']);

/**
 * Get the normalized/canonical stat label for a given stat name.
 * Returns the original stat if no mapping exists.
 */
export function normalizeStatLabel(stat: string): string {
  return STAT_LABELS[stat] || stat;
}

const NHL_2_DECIMAL_STATS = new Set(['AST', 'BLK', 'GAA', 'G', 'PTS', 'SOG']);
// Low-count NFL markets where hundredths matter (0.45 TDs); yardage/volume stats read fine at one decimal.
const NFL_2_DECIMAL_STATS = new Set(['PASS TDS', 'PASS TD', 'RUSH TDS', 'RUSH TD', 'REC TDS', 'REC TD', 'ANY TD', 'RUSH+REC TDS', 'TOTAL TDS', 'INT', 'INT CAUGHT', 'FUM LOST', 'SACKS', 'FGM', 'XPM']);

export const getStatDecimals = (stat: string, league: string): number => {
  const leagueUpper = league.toUpperCase();
  if (leagueUpper === 'NHL' && NHL_2_DECIMAL_STATS.has(stat.toUpperCase())) return 2;
  if (leagueUpper === 'MLB') return 2;
  if (leagueUpper === 'NFL' && NFL_2_DECIMAL_STATS.has(stat.toUpperCase())) return 2;
  return 1;
};
